"use client";

import { useState } from "react";
import { getSiteContent } from "@/content";
import { type Locale, localizedPath } from "@/lib/site";
import { SharedCta } from "@/components/ui/SharedCta";

export function WarehousePage({ locale }: { locale: Locale }) {
  const t = getSiteContent(locale).pages.warehouse;
  const [copied, setCopied] = useState<string | null>(null);

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
    }
    setCopied(text);
    window.setTimeout(() => setCopied(null), 1800);
  }

  return (
    <main className="mx-auto min-h-screen max-w-container-max overflow-x-hidden px-margin-mobile pb-section-gap pt-[156px] md:px-margin-desktop md:pt-[168px]">
      <section className="relative mb-section-gap text-center md:text-left">
        <div className="absolute -left-20 -top-20 -z-10 h-96 w-96 rounded-full bg-secondary/5 blur-3xl" />
        <div className="mb-stack-md inline-flex items-center gap-2 rounded-full border border-secondary/20 bg-secondary-container/10 px-4 py-1.5 text-secondary">
          <span className="material-symbols-outlined text-[18px]">location_on</span>
          <span className="font-label-md text-label-md">{t.heroBadge}</span>
        </div>
        <h1 className="mb-stack-md font-display-lg text-display-lg-mobile leading-tight text-primary md:text-display-lg">{t.title}</h1>
        <p className="max-w-2xl font-body-lg text-body-lg leading-relaxed text-text-secondary">{t.subtitle}</p>
      </section>

      <div className="grid grid-cols-1 items-start gap-gutter lg:grid-cols-12">
        <section className="overflow-hidden rounded-card border border-border-pale bg-white shadow-[0_4px_24px_rgba(0,43,91,0.04)] lg:col-span-7">
          <div className="marine-gradient flex flex-col gap-4 p-8 text-white sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-headline-md text-headline-md">{t.cardTitle}</h2>
              <p className="font-body-sm text-body-sm opacity-80">{t.cardSubtitle}</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                className={`inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 font-label-md text-white backdrop-blur-md transition-all hover:bg-white/20 ${copied === t.copyAllText ? "border-success bg-success/10" : ""}`}
                onClick={() => copyText(t.copyAllText)}
                type="button"
                aria-label={t.copyAllAria}
              >
                <span className="material-symbols-outlined text-[18px]">{copied === t.copyAllText ? "check_circle" : "content_copy"}</span>
                <span>{copied === t.copyAllText ? t.copiedLabel : t.copyAllLabel}</span>
              </button>
              <span className="material-symbols-outlined hidden text-[48px] opacity-20 sm:block">warehouse</span>
            </div>
          </div>
          <div className="space-y-stack-lg p-8">
            {t.fields.map((field) => (
              <div className="group" key={field.label}>
                <label className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-text-secondary">{field.label}</label>
                <div className={`relative flex rounded-xl border border-transparent bg-surface-container-low p-4 transition-all duration-300 group-hover:border-secondary/30 ${"multiline" in field && field.multiline ? "items-start gap-4" : "items-center"}`}>
                  <span className={`${"multiline" in field && field.multiline ? "block flex-1 leading-relaxed" : "font-medium"} text-primary ${"emphasis" in field && field.emphasis ? "font-headline-md text-secondary font-bold" : "font-body-lg"}`}>
                    {"valuePrefix" in field ? field.valuePrefix : null}
                    {"value" in field ? field.value : null}
                    {"valueHighlight" in field ? (
                      <>
                        {"multiline" in field && field.multiline ? <br /> : null}
                        <span className="font-bold text-parcel-orange">{field.valueHighlight}</span>
                      </>
                    ) : null}
                  </span>
                  <button className={`ml-auto text-secondary transition-transform hover:scale-110 ${copied === field.copy ? "text-success" : ""}`} onClick={() => copyText(field.copy)} type="button" aria-label={`${t.copyFieldAriaPrefix}${field.label}`}>
                    <span className="material-symbols-outlined">{copied === field.copy ? "check_circle" : "content_copy"}</span>
                  </button>
                </div>
                {"warning" in field && field.warning ? (
                  <p className="mt-2 flex items-center gap-1 font-label-sm text-label-sm text-error">
                    <span className="material-symbols-outlined text-[14px]">warning</span>
                    <span>{field.warning}</span>
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </section>

        <aside className="space-y-gutter lg:col-span-5">
          <section className="rounded-card border border-border-pale bg-white p-8 shadow-[0_4px_24px_rgba(0,43,91,0.04)]">
            <h2 className="mb-stack-lg flex items-center gap-2 font-headline-md text-headline-md text-primary">
              <span className="material-symbols-outlined text-secondary">help</span>
              {t.example.title}
            </h2>
            <div className="space-y-stack-lg">
              <div className="rounded-xl border border-secondary/10 bg-glacier-white p-4">
                <div className="mb-4 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-parcel-orange/10">
                    <span className="material-symbols-outlined text-[20px] text-parcel-orange">shopping_cart</span>
                  </div>
                  <span className="font-label-md text-label-md font-bold text-primary">{t.example.platform}</span>
                </div>
                <div className="relative aspect-video overflow-hidden rounded-lg bg-surface-variant">
                  <img className="h-full w-full object-cover" src="/assets/images/warehouse-hub.png" alt={t.example.alt} width={1200} height={760} />
                  <div className="absolute inset-0 flex items-center justify-center bg-primary/40 opacity-0 transition-opacity hover:opacity-100">
                    <span className="flex items-center gap-2 rounded-full bg-white px-4 py-2 font-label-md text-primary">
                      <span className="material-symbols-outlined">zoom_in</span>
                      {t.example.zoom}
                    </span>
                  </div>
                </div>
                <p className="mt-3 font-body-sm text-body-sm italic text-text-secondary">{t.example.tip}</p>
              </div>
              <div className="flex items-start gap-4 rounded-xl border border-dashed border-secondary/30 p-4">
                <span className="material-symbols-outlined text-secondary">info</span>
                <div>
                  <h3 className="font-label-md text-label-md font-bold text-primary">{t.example.batchTitle}</h3>
                  <p className="mt-1 font-body-sm text-body-sm text-text-secondary">{t.example.batchCopy}</p>
                </div>
              </div>
            </div>
          </section>
        </aside>
      </div>

      <section className="mt-section-gap">
        <h2 className="mb-stack-lg text-center font-headline-md text-headline-md text-primary">{t.notesTitle}</h2>
        <div className="grid grid-cols-1 gap-gutter md:grid-cols-3">
          {t.notes.map(([icon, title, copy, shellClass, iconClass, hoverClass]) => (
            <article className={`group rounded-card border border-border-pale bg-white p-6 transition-all ${hoverClass}`} key={title}>
              <div className={`ui-icon-round ui-icon-tile-md mb-4 transition-transform group-hover:scale-110 ${shellClass}`}>
                <span className={`material-symbols-outlined ${iconClass}`}>{icon}</span>
              </div>
              <h3 className="mb-2 font-headline-md text-[18px] text-primary">{title}</h3>
              <p className="whitespace-pre-line font-body-sm text-body-sm text-text-secondary">{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <SharedCta
        className="mt-section-gap"
        title={t.cta.title}
        copy={t.cta.copy}
        actions={[
          { label: t.cta.actions[0], href: localizedPath(locale, "tools/parcel-prealert"), variant: "primary" },
          { label: t.cta.actions[1], href: localizedPath(locale, "tools/shipping-calculator"), variant: "secondary" }
        ]}
      />
    </main>
  );
}
